import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { LocalStorageService, SITE_BOOKING_SETTINGS } from '@app/services/local-storage.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SelectDateTimeService } from '../../../../../app/components/booking-steps/select-date-time/select-date-time.service';

@Component({
    selector: 'custom-calendar-header',
    styleUrls: ['custom-calendar-header.component.scss'],
    templateUrl: 'custom-calendar-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CustomCalendarHeaderComponent<D> implements OnInit, OnDestroy {
    private _destroyed = new Subject<void>();
    @Output() isNextClicked = new EventEmitter<string>();

    constructor(
        private _calendar: MatCalendar<D>,
        private _dateAdapter: DateAdapter<D>,
        @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
        cdr: ChangeDetectorRef,
        private selectDateTimeService: SelectDateTimeService,
        private localStorageService: LocalStorageService
    ) {
        _calendar.stateChanges.pipe(takeUntil(this._destroyed)).subscribe(() => cdr.markForCheck());
    }

    ngOnInit() {
        /* istanbul ignore next */
        this.selectDateTimeService.onCalendarDataChanged.subscribe(() => {
            this._calendar.updateTodaysDate();
        });
    }

    ngOnDestroy() {
        this._destroyed.next();
        this._destroyed.complete();
    }

    get periodLabel() {
        return this._dateAdapter.format(this._calendar.activeDate, this._dateFormats.display.monthYearA11yLabel);
    }

    previousClicked() {
        this.selectDateTimeService.previousClicked(this._calendar.activeDate);
        this._calendar.activeDate = this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1);
    }

    /**
     * Executes the logic that checks if the left chevron in the calendar should be disabled or not
     * @returns A boolean value that controls if the left chevron in the calendar should be disabled or not
     */
    previousEnabled() {
        const activeDay = this._dateAdapter.today();
        const newActiveDate = this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1);
        const activeDayDateObj = new Date(activeDay as unknown as Date);
        const newActiveDateObj = new Date(newActiveDate as unknown as Date);
        if (
            newActiveDateObj.getFullYear() > activeDayDateObj.getFullYear() ||
            (newActiveDateObj.getFullYear() === activeDayDateObj.getFullYear() &&
                newActiveDateObj.getMonth() >= activeDayDateObj.getMonth())
        ) {
            return false;
        }
        return true;
    }

    /**
     * Executes the logic that checks if the right chevron in the calendar should be disabled or not
     * @returns A boolean value that controls if the right chevron in the calendar should be disabled or not
     */
    /* istanbul ignore next */
    nextEnabled() {
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        if (!siteBookingSettings?.appointmentMaxAdvanceBooking) {
            return;
        }
        const activeDay = this._calendar.activeDate;
        const newActiveDate = this._dateAdapter.addCalendarDays(
            this._dateAdapter.today(),
            siteBookingSettings.appointmentMaxAdvanceBooking
        );
        const activeDayDateObj = new Date(activeDay as unknown as Date);
        const newActiveDateObj = new Date(newActiveDate as unknown as Date);
        if (
            activeDayDateObj.getFullYear() === newActiveDateObj.getFullYear() &&
            activeDayDateObj.getMonth() === newActiveDateObj.getMonth()
        ) {
            return true;
        }
        return false;
    }

    nextClicked() {
        this.selectDateTimeService.nextClicked(this._calendar.activeDate);
        this._calendar.activeDate = this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1);
    }
}
